import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Toaster } from "../components/ui/sonner";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Crown, BookOpen, Lightbulb, Scale, DollarSign, Flag, Landmark,
  Users2, Briefcase, GraduationCap, Award, HandHeart, Building2,
  ChevronDown, ShieldCheck, ArrowRight, Sparkles, Heart, Store as StoreIcon,
} from "lucide-react";

const CREST = "/images/crown_institute_crest.png";

// ===== PROGRAMS — each maps to an AcademyDetail slug or framework page =====
const PROGRAMS = [
  { id: "k12",       to: "/academy/civic-duty-foundation",            title: "K–12 Leadership Curriculum",                    Icon: Users2,       blurb: "Core leadership, character, and civic foundations for grades K–12." },
  { id: "entre",     to: "/academy/entrepreneur-academy",              title: "Entrepreneur Academy",                          Icon: Lightbulb,    blurb: "Business formation, credit, cash flow, taxes, and financial readiness." },
  { id: "finlit",    to: "/academy/financial-literacy",                title: "Financial Literacy Semester",                   Icon: DollarSign,   blurb: "16-week program covering personal & business money mastery." },
  { id: "civic",     to: "/academy/civic-duty-foundation",             title: "Civic Duty Semester",                           Icon: Flag,         blurb: "Local, state, federal government — roles, responsibilities, citizen action." },
  { id: "amhist",    to: "/academy/american-history-foundation",       title: "American History Foundations",                  Icon: Landmark,     blurb: "Semester 1 of the Civic Leadership Track." },
  { id: "constit",   to: "/academy/constitution-and-rights",           title: "Constitution and Rights",                       Icon: Scale,        blurb: "Semester 2 — rights, amendments, and constitutional principles." },
  { id: "budget",    to: "/academy/public-budget-and-taxation",        title: "Public Budget and Taxation",                    Icon: Landmark,     blurb: "Semester 3 — how public budgets work and why they matter." },
  { id: "community", to: "/academy/community-leadership-internship",   title: "Community Leadership & Public Service",         Icon: HandHeart,    blurb: "Semester 4 — capstone civic leadership project." },
  { id: "intern",    to: "/academy/community-leadership-internship",   title: "Internship Program",                            Icon: Briefcase,    blurb: "40+ hour placements in city, county, nonprofit, and partner organizations." },
  { id: "sals",      to: "/frameworks",                                title: "SALS Curriculum",                               Icon: BookOpen,     blurb: "Strategic Adaptive Leadership System — core leadership framework." },
  { id: "chet",      to: "/frameworks",                                title: "CHET Protocols",                                Icon: ShieldCheck,  blurb: "Character · Honor · Excellence · Tenacity discipline protocols." },
  { id: "5pillars",  to: "/frameworks",                                title: "Five Pillars",                                  Icon: Sparkles,     blurb: "Five-pillar values framework — faith, family, finance, future, freedom." },
  { id: "331",       to: "/frameworks",                                title: "3+3+1 Theory",                                  Icon: Award,        blurb: "Three skills, three habits, one mission — the legacy equation." },
];

const FINLIT_WEEKS = [
  "Personal vs business money", "Business entity types", "LLC formation",
  "EIN registration", "Opening business bank accounts", "Building banking relationships",
  "Business credit", "Bookkeeping", "Cash flow", "Taxes", "Financial statements",
  "Budgeting", "Savings and reserves", "Loans and financing",
  "Financial communication", "Final readiness plan",
];

const ENTRE_TOPICS = [
  "Business formation", "LLC setup", "EIN registration",
  "Business bank accounts", "Banking relationships", "Business credit",
  "Budgeting", "Bookkeeping", "Cash flow", "Taxes",
  "Financial statements", "Business planning",
];

const CIVIC_TRACK = [
  { n: 1, title: "American History Foundations" },
  { n: 2, title: "Constitution and Rights" },
  { n: 3, title: "Public Budget and Taxation" },
  { n: 4, title: "Community Leadership & Public Service" },
];

const CIVIC_DUTY_EXAMS = [
  "Civic Duty Fundamentals", "Local Government", "State Government",
  "Federal Government", "Intergovernmental Cooperation", "Final Civic Duty Exam",
];

const INTERNSHIP_PLACEMENTS = [
  "City government", "County government", "Nonprofit organization",
  "Public works", "Public safety", "Infrastructure office",
  "Community service organization", "Business partner", "Financial institution partner",
];

// ===== FAQ =====
const FAQ = [
  { q: "Is Crown Institute Academy part of AuditIQ?",
    a: "Yes — Crown Institute Academy is the education division of AuditIQ. Both operate on the same platform, curriculum infrastructure, and administrative backend." },
  { q: "What is the passing score?",
    a: "All Crown Institute programs use a 1-10 grading scale. Students must score 9.2 or higher to advance. Any score below 9.2 triggers a reset, unlocks a remediation lesson, and requires a retest." },
  { q: "How long is the Entrepreneur Academy?",
    a: "Entrepreneur Academy is an ongoing program with a $499 initiation/enrollment fee and $99 per semester. It culminates in a Business Financial Readiness Plan project." },
  { q: "What is the internship requirement?",
    a: "Every student must complete a minimum of 40 internship hours placed with a city, county, nonprofit, public works office, business partner, or financial institution partner." },
  { q: "Can companies sponsor students?",
    a: "Yes. Crown Institute welcomes corporate sponsorships, monthly donations, and program-specific giving. See the Sponsors & Donations section below." },
];


// ===== PAGE =====
const CrownInstituteAcademy = () => {
  const [meta, setMeta] = useState({ programs: [], donation_causes: [], sponsorship_levels: [], placement_types: [] });
  const [tab, setTab] = useState("enroll");
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/academy/meta").then(r => setMeta(r.data)).catch(() => {});
  }, []);

  // ---- Forms ----
  const [enroll, setEnroll] = useState({ first_name: "", last_name: "", email: "", phone: "", program: "Entrepreneur Academy", grade_level: "", city: "", state: "", parent_name: "", parent_email: "", parent_phone: "", notes: "" });
  const [parentIn, setParentIn] = useState({ name: "", email: "", phone: "", student_name: "", student_grade: "", message: "" });
  const [teacherIn, setTeacherIn] = useState({ first_name: "", last_name: "", email: "", phone: "", qualifications: "", subjects: "", resume_url: "" });
  const [sponsor, setSponsor] = useState({ contact_name: "", organization: "", email: "", phone: "", sponsorship_level: "", interest_area: "", message: "" });
  const [donate, setDonate] = useState({ donor_name: "", email: "", phone: "", cause: "", amount: "", message: "", recurring: false });
  const [intern, setIntern] = useState({ organization: "", contact_name: "", email: "", phone: "", placement_type: "", capacity: "", notes: "" });

  const post = (path, body, reset) => async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(path, body);
      toast.success(r.data.message || "Submitted");
      reset && reset();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string"
        ? d
        : Array.isArray(d)
          ? d.map(x => x?.msg || JSON.stringify(x)).join(", ")
          : "Submit failed";
      toast.error(msg);
    }
  };

  const scrollTo = (id) => () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "EducationalOrganization", name: "Crown Institute Academy",
        parentOrganization: { "@type": "Organization", name: "AuditIQ" },
        description: "Leadership, entrepreneurship, civic duty, and financial literacy education powered by AuditIQ." },
      { "@type": "Course", name: "Entrepreneur Academy", provider: { "@type": "Organization", name: "Crown Institute Academy" },
        offers: { "@type": "Offer", price: "499", priceCurrency: "USD", description: "Initiation fee. $99 per semester." } },
      { "@type": "FAQPage", mainEntity: FAQ.map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
    ],
  };

  return (
    <Layout>
      <SEOHead
        title="Crown Institute Academy — Leadership, Entrepreneurship, Civic Duty | AuditIQ"
        description="Crown Institute Academy is the education division of AuditIQ — K-12 leadership, entrepreneur academy, financial literacy, civic duty, and internship programs with a 9.2 pass-score standard."
        path="/crown-institute-academy"
        image={CREST}
        schema={schema}
      />
      <Toaster position="top-right" richColors theme="dark" />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="academy-hero">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_85%,rgba(201,168,76,0.18),transparent_55%),radial-gradient(circle_at_85%_15%,rgba(45,156,219,0.12),transparent_55%)]" />
        <div className="absolute inset-0 cyber-grid opacity-30" />
        <div className="container-x relative pt-24 pb-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-gold">
              <Crown size={12} /> Crown Institute Academy
            </div>
            <h1 className="font-display text-4xl sm:text-5xl mt-5 leading-[1.05]">
              Educate.<br />
              <span className="text-gold">Empower.</span><br />
              Elevate.
            </h1>
            <p className="text-cream-dim text-base lg:text-lg mt-6 max-w-xl">
              Leadership, entrepreneurship, civic duty, and financial literacy — taught at a <span className="text-gold">9.2 pass-score</span> standard.
              Crown Institute Academy is the education division of AuditIQ.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 btn-stack">
              <Button onClick={scrollTo("enroll-section")} className="btn-gold h-12 px-6 text-sm" data-testid="academy-enroll-cta">
                Enroll Now <ArrowRight size={14} className="ml-2" />
              </Button>
              <Link to="/student-login" className="contents">
                <Button className="btn-ghost-gold h-12 px-6 text-sm w-full sm:w-auto" data-testid="academy-student-cta">Student Login</Button>
              </Link>
              <Button onClick={scrollTo("sponsor-section")} className="btn-ghost-gold h-12 px-6 text-sm" data-testid="academy-sponsor-cta">
                Sponsor a Student <Heart size={14} className="ml-2" />
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-2 text-[11px] uppercase tracking-[0.2em] text-steel">
              <span className="flex items-center gap-2"><Crown size={12} /> Three Pillars</span>
              <span className="flex items-center gap-2"><GraduationCap size={12} /> K–12 · Semesters · Internship</span>
              <span className="flex items-center gap-2"><ShieldCheck size={12} /> 9.2 Pass Standard</span>
            </div>
          </div>
          <div className="relative flex justify-center">
            <div className="absolute -inset-4 bg-gradient-to-br from-gold/30 via-electric/10 to-transparent blur-3xl opacity-60 pointer-events-none" />
            <img src={CREST} alt="Crown Institute Academy crest"
              className="relative max-h-[520px] w-auto"
              data-testid="academy-crest-image" />
          </div>
        </div>
      </section>

      {/* PROGRAMS GRID */}
      <section className="bg-navy-deep border-y border-gold-dim/20 py-16">
        <div className="container-x">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Curriculum</div>
          <h2 className="font-display text-3xl md:text-4xl mt-3">13 Programs. One Mission.</h2>
          <p className="text-cream-dim mt-3 max-w-2xl">A complete education pathway from K–12 foundations to entrepreneurial and civic leadership mastery.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="academy-programs-grid">
            {PROGRAMS.map(p => (
              <Link
                key={p.id}
                to={p.to}
                className="glass p-5 hover:border-gold transition-colors block"
                data-testid={`program-${p.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded bg-navy-deep border border-gold-dim/30 flex items-center justify-center shrink-0">
                    <p.Icon size={16} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="text-cream font-medium">{p.title}</h3>
                    <p className="text-cream-dim text-sm mt-1">{p.blurb}</p>
                    <div className="text-electric text-xs mt-3 flex items-center gap-1">
                      Open program <ArrowRight size={12} />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ENTREPRENEUR ACADEMY */}
      <section className="py-16">
        <div className="container-x grid lg:grid-cols-[1fr_1fr] gap-10 items-start">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Flagship Program</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3">Entrepreneur Academy</h2>
            <p className="text-cream-dim mt-3 max-w-lg">Business formation through financial mastery — with a capstone Business Financial Readiness Plan.</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="glass p-4"><div className="text-[10px] uppercase tracking-[0.22em] text-steel">Initiation fee</div><div className="font-display text-2xl text-gold mt-1">$499</div></div>
              <div className="glass p-4"><div className="text-[10px] uppercase tracking-[0.22em] text-steel">Per semester</div><div className="font-display text-2xl text-gold mt-1">$99</div></div>
            </div>
            <div className="glass p-5 mt-4 border-l-2 border-gold">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Final Project</div>
              <div className="text-cream font-medium mt-1">Business Financial Readiness Plan</div>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-steel mb-3">Curriculum topics</div>
            <div className="grid grid-cols-2 gap-2">
              {ENTRE_TOPICS.map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-cream">
                  <div className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINANCIAL LITERACY 16 WEEKS */}
      <section className="bg-navy-deep border-y border-gold-dim/20 py-16">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-electric">16-Week Semester</div>
              <h2 className="font-display text-3xl md:text-4xl mt-3">Financial Literacy Semester</h2>
              <p className="text-cream-dim mt-3 max-w-lg">
                Every week includes a lesson plan, assignment, quiz, grading workflow, and remediation flow if the score falls below 9.2.
              </p>
            </div>
            <div className="glass p-4 text-center">
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Weeks</div>
              <div className="font-display text-4xl text-gold mt-1">16</div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            {FINLIT_WEEKS.map((w, i) => (
              <div key={i} className="glass p-3 flex items-start gap-3" data-testid={`finlit-week-${i + 1}`}>
                <span className="text-gold font-display text-lg w-6 shrink-0">{i + 1}</span>
                <span className="text-cream text-sm">{w}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CIVIC DUTY + 4-SEMESTER TRACK */}
      <section className="py-16">
        <div className="container-x grid lg:grid-cols-2 gap-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Civic Duty Semester</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3">Local · State · Federal</h2>
            <p className="text-cream-dim mt-3">
              Students learn government structure and citizen responsibility in a deliberate sequence, finishing with a comprehensive final exam.
            </p>
            <div className="mt-6 space-y-2">
              {CIVIC_DUTY_EXAMS.map((e, i) => (
                <div key={i} className="glass p-3 flex items-center gap-3 text-sm text-cream">
                  <Scale size={14} className="text-gold shrink-0" /> {e}
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Civic Leadership Track</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3">Four Semesters</h2>
            <div className="mt-6 space-y-3">
              {CIVIC_TRACK.map(s => (
                <div key={s.n} className="glass p-4 flex items-center gap-4" data-testid={`civic-semester-${s.n}`}>
                  <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold flex items-center justify-center shrink-0">
                    <span className="font-display text-xl text-gold">{s.n}</span>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Semester {s.n}</div>
                    <div className="text-cream font-medium">{s.title}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INTERNSHIP */}
      <section className="bg-navy-deep border-y border-gold-dim/20 py-16">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Internship Program</div>
              <h2 className="font-display text-3xl md:text-4xl mt-3">40+ Hour Placements</h2>
              <p className="text-cream-dim mt-3 max-w-lg">Every student tracks hours, supervisor, weekly logs, final report, and evaluation.</p>
            </div>
            <div className="glass p-4 text-center">
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Minimum hours</div>
              <div className="font-display text-4xl text-gold mt-1">40</div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
            {INTERNSHIP_PLACEMENTS.map((p, i) => (
              <div key={i} className="glass p-4 flex items-center gap-3" data-testid={`internship-placement-${i}`}>
                <Building2 size={14} className="text-electric shrink-0" />
                <span className="text-cream text-sm">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GRADING SYSTEM */}
      <section className="py-16">
        <div className="container-x max-w-4xl">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Grading Standard</div>
          <h2 className="font-display text-3xl md:text-4xl mt-3">9.2 Pass Score — No Exceptions</h2>
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            <div className="glass p-6 text-center">
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Scale</div>
              <div className="font-display text-5xl text-gold mt-2">1–10</div>
            </div>
            <div className="glass p-6 text-center">
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Passing</div>
              <div className="font-display text-5xl text-[color:var(--auditiq-success)] mt-2">9.2</div>
              <div className="text-[10px] text-cream-dim mt-1">Minimum</div>
            </div>
            <div className="glass p-6 text-center">
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Below 9.2</div>
              <div className="font-display text-xl text-[color:var(--auditiq-warning)] mt-2">Lesson resets</div>
              <div className="text-[10px] text-cream-dim mt-1">Remediation unlocks · retest required · progress locked</div>
            </div>
          </div>
        </div>
      </section>

      {/* ENROLL / PARENT / TEACHER TABS */}
      <section id="enroll-section" className="bg-navy-deep border-y border-gold-dim/20 py-16">
        <div className="container-x max-w-4xl">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Admissions</div>
          <h2 className="font-display text-3xl md:text-4xl mt-3">Join Crown Institute Academy</h2>
          <div className="flex border-b border-gold-dim/20 gap-1 mt-6">
            {[
              ["enroll",  "Enroll a Student"],
              ["parent",  "Parent Inquiry"],
              ["teacher", "Teacher's Aide"],
            ].map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)}
                data-testid={`academy-tab-${id}`}
                className={`px-4 py-2.5 text-xs uppercase tracking-[0.18em] transition-colors ${
                  tab === id ? "text-gold border-b-2 border-gold" : "text-cream-dim hover:text-gold"
                }`}>{label}</button>
            ))}
          </div>

          {tab === "enroll" && (
            <form onSubmit={post("/academy/enroll", enroll,
                () => setEnroll({ first_name: "", last_name: "", email: "", phone: "", program: "Entrepreneur Academy", grade_level: "", city: "", state: "", parent_name: "", parent_email: "", parent_phone: "", notes: "" }))}
              className="glass p-6 mt-6 grid md:grid-cols-2 gap-3" data-testid="enroll-form">
              <Input required placeholder="First Name" value={enroll.first_name} onChange={e => setEnroll({ ...enroll, first_name: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="enroll-first" />
              <Input required placeholder="Last Name" value={enroll.last_name} onChange={e => setEnroll({ ...enroll, last_name: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="enroll-last" />
              <Input required type="email" placeholder="Student Email" value={enroll.email} onChange={e => setEnroll({ ...enroll, email: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="enroll-email" />
              <Input placeholder="Phone" value={enroll.phone} onChange={e => setEnroll({ ...enroll, phone: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Select value={enroll.program} onValueChange={v => setEnroll({ ...enroll, program: v })}>
                <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream md:col-span-2" data-testid="enroll-program"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                  {(meta.programs || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="Grade Level" value={enroll.grade_level} onChange={e => setEnroll({ ...enroll, grade_level: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Input placeholder="City" value={enroll.city} onChange={e => setEnroll({ ...enroll, city: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Input placeholder="Parent / Guardian Name" value={enroll.parent_name} onChange={e => setEnroll({ ...enroll, parent_name: e.target.value })} className="bg-navy-deep border-gold-dim/40 md:col-span-2" />
              <Input type="email" placeholder="Parent Email" value={enroll.parent_email} onChange={e => setEnroll({ ...enroll, parent_email: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Input placeholder="Parent Phone" value={enroll.parent_phone} onChange={e => setEnroll({ ...enroll, parent_phone: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Textarea rows={3} placeholder="Additional notes" value={enroll.notes} onChange={e => setEnroll({ ...enroll, notes: e.target.value })} className="bg-navy-deep border-gold-dim/40 text-cream md:col-span-2" />
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" className="btn-gold h-11 px-6" data-testid="enroll-submit">Submit Enrollment</Button>
              </div>
            </form>
          )}

          {tab === "parent" && (
            <form onSubmit={post("/academy/parent-inquiry", parentIn,
                () => setParentIn({ name: "", email: "", phone: "", student_name: "", student_grade: "", message: "" }))}
              className="glass p-6 mt-6 grid md:grid-cols-2 gap-3" data-testid="parent-form">
              <Input required placeholder="Parent Name" value={parentIn.name} onChange={e => setParentIn({ ...parentIn, name: e.target.value })} className="bg-navy-deep border-gold-dim/40 md:col-span-2" />
              <Input required type="email" placeholder="Email" value={parentIn.email} onChange={e => setParentIn({ ...parentIn, email: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Input placeholder="Phone" value={parentIn.phone} onChange={e => setParentIn({ ...parentIn, phone: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Input placeholder="Student Name" value={parentIn.student_name} onChange={e => setParentIn({ ...parentIn, student_name: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Input placeholder="Student Grade" value={parentIn.student_grade} onChange={e => setParentIn({ ...parentIn, student_grade: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Textarea required rows={4} placeholder="How can we help?" value={parentIn.message} onChange={e => setParentIn({ ...parentIn, message: e.target.value })} className="bg-navy-deep border-gold-dim/40 text-cream md:col-span-2" />
              <div className="md:col-span-2 flex justify-end"><Button type="submit" className="btn-gold h-11 px-6" data-testid="parent-submit">Send Inquiry</Button></div>
            </form>
          )}

          {tab === "teacher" && (
            <form onSubmit={post("/academy/teacher-application", teacherIn,
                () => setTeacherIn({ first_name: "", last_name: "", email: "", phone: "", qualifications: "", subjects: "", resume_url: "" }))}
              className="glass p-6 mt-6 grid md:grid-cols-2 gap-3" data-testid="teacher-form">
              <Input required placeholder="First Name" value={teacherIn.first_name} onChange={e => setTeacherIn({ ...teacherIn, first_name: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Input required placeholder="Last Name" value={teacherIn.last_name} onChange={e => setTeacherIn({ ...teacherIn, last_name: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Input required type="email" placeholder="Email" value={teacherIn.email} onChange={e => setTeacherIn({ ...teacherIn, email: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Input placeholder="Phone" value={teacherIn.phone} onChange={e => setTeacherIn({ ...teacherIn, phone: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Input required placeholder="Subjects (comma separated)" value={teacherIn.subjects} onChange={e => setTeacherIn({ ...teacherIn, subjects: e.target.value })} className="bg-navy-deep border-gold-dim/40 md:col-span-2" />
              <Textarea required rows={4} placeholder="Qualifications & experience" value={teacherIn.qualifications} onChange={e => setTeacherIn({ ...teacherIn, qualifications: e.target.value })} className="bg-navy-deep border-gold-dim/40 text-cream md:col-span-2" />
              <Input placeholder="Resume URL (optional)" value={teacherIn.resume_url} onChange={e => setTeacherIn({ ...teacherIn, resume_url: e.target.value })} className="bg-navy-deep border-gold-dim/40 md:col-span-2" />
              <div className="md:col-span-2 flex justify-end"><Button type="submit" className="btn-gold h-11 px-6" data-testid="teacher-submit">Submit Application</Button></div>
            </form>
          )}
        </div>
      </section>

      {/* SPONSORSHIP + DONATION + INTERNSHIP PARTNER */}
      <section id="sponsor-section" className="py-16">
        <div className="container-x">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Sponsor */}
            <form onSubmit={post("/academy/sponsor-inquiry", sponsor,
                () => setSponsor({ contact_name: "", organization: "", email: "", phone: "", sponsorship_level: "", interest_area: "", message: "" }))}
              className="glass p-6 space-y-3" data-testid="sponsor-form">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold">
                <Award size={12} /> Sponsor
              </div>
              <h3 className="font-display text-xl">Become a Sponsor</h3>
              <Input required placeholder="Contact Name" value={sponsor.contact_name} onChange={e => setSponsor({ ...sponsor, contact_name: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="sponsor-name" />
              <Input required placeholder="Organization" value={sponsor.organization} onChange={e => setSponsor({ ...sponsor, organization: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="sponsor-org" />
              <Input required type="email" placeholder="Email" value={sponsor.email} onChange={e => setSponsor({ ...sponsor, email: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="sponsor-email" />
              <Input placeholder="Phone" value={sponsor.phone} onChange={e => setSponsor({ ...sponsor, phone: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Select value={sponsor.sponsorship_level} onValueChange={v => setSponsor({ ...sponsor, sponsorship_level: v })}>
                <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="sponsor-level"><SelectValue placeholder="Sponsorship level" /></SelectTrigger>
                <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                  {(meta.sponsorship_levels || []).map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Textarea rows={2} placeholder="Interest area / message" value={sponsor.message} onChange={e => setSponsor({ ...sponsor, message: e.target.value })} className="bg-navy-deep border-gold-dim/40 text-cream" />
              <Button type="submit" className="btn-gold h-10 w-full text-xs" data-testid="sponsor-submit">Send Sponsor Inquiry</Button>
            </form>

            {/* Donate */}
            <form onSubmit={post("/academy/donation-inquiry", donate,
                () => setDonate({ donor_name: "", email: "", phone: "", cause: "", amount: "", message: "", recurring: false }))}
              className="glass p-6 space-y-3" data-testid="donate-form">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold">
                <Heart size={12} /> Donate
              </div>
              <h3 className="font-display text-xl">Make a Gift</h3>
              <p className="text-[10px] text-cream-dim">Payment processor to be configured before go-live. This form currently records your intent and contacts you.</p>
              <Input required placeholder="Your Name" value={donate.donor_name} onChange={e => setDonate({ ...donate, donor_name: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="donate-name" />
              <Input required type="email" placeholder="Email" value={donate.email} onChange={e => setDonate({ ...donate, email: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="donate-email" />
              <Select value={donate.cause} onValueChange={v => setDonate({ ...donate, cause: v })}>
                <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="donate-cause"><SelectValue placeholder="Select cause" /></SelectTrigger>
                <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                  {(meta.donation_causes || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="Amount (USD, optional)" value={donate.amount} onChange={e => setDonate({ ...donate, amount: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <label className="flex items-center gap-2 text-sm text-cream">
                <input type="checkbox" checked={donate.recurring} onChange={e => setDonate({ ...donate, recurring: e.target.checked })} data-testid="donate-recurring" />
                Make this a monthly gift
              </label>
              <Textarea rows={2} placeholder="Message (optional)" value={donate.message} onChange={e => setDonate({ ...donate, message: e.target.value })} className="bg-navy-deep border-gold-dim/40 text-cream" />
              <Button type="submit" className="btn-gold h-10 w-full text-xs" data-testid="donate-submit">Send Donation Inquiry</Button>
            </form>

            {/* Internship partner */}
            <form onSubmit={post("/academy/internship-partner", intern,
                () => setIntern({ organization: "", contact_name: "", email: "", phone: "", placement_type: "", capacity: "", notes: "" }))}
              className="glass p-6 space-y-3" data-testid="intern-form">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold">
                <Briefcase size={12} /> Internship Partner
              </div>
              <h3 className="font-display text-xl">Host an Intern</h3>
              <Input required placeholder="Organization" value={intern.organization} onChange={e => setIntern({ ...intern, organization: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="intern-org" />
              <Input required placeholder="Contact Name" value={intern.contact_name} onChange={e => setIntern({ ...intern, contact_name: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Input required type="email" placeholder="Email" value={intern.email} onChange={e => setIntern({ ...intern, email: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="intern-email" />
              <Input placeholder="Phone" value={intern.phone} onChange={e => setIntern({ ...intern, phone: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Select value={intern.placement_type} onValueChange={v => setIntern({ ...intern, placement_type: v })}>
                <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="intern-type"><SelectValue placeholder="Placement type" /></SelectTrigger>
                <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                  {(meta.placement_types || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="Interns you can host (capacity)" value={intern.capacity} onChange={e => setIntern({ ...intern, capacity: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
              <Textarea rows={2} placeholder="Notes" value={intern.notes} onChange={e => setIntern({ ...intern, notes: e.target.value })} className="bg-navy-deep border-gold-dim/40 text-cream" />
              <Button type="submit" className="btn-gold h-10 w-full text-xs" data-testid="intern-submit">Become a Partner</Button>
            </form>
          </div>
        </div>
      </section>

      {/* STORE CTA */}
      <section className="bg-navy-deep border-y border-gold-dim/20 py-12">
        <div className="container-x flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <StoreIcon size={28} className="text-gold" />
            <div>
              <h3 className="font-display text-2xl">Crown Institute Store</h3>
              <p className="text-cream-dim text-sm mt-1">Workbooks · manuals · leadership journals · financial literacy materials · apparel.</p>
            </div>
          </div>
          <Link to="/store"><Button className="btn-ghost-gold h-11 px-5 text-xs" data-testid="academy-store-link">Visit Store →</Button></Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container-x max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Frequently asked</div>
          <h2 className="font-display text-3xl md:text-4xl mt-3">Academy FAQ</h2>
          <div className="mt-8 space-y-2" data-testid="academy-faq">
            {FAQ.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="glass overflow-hidden">
                  <button onClick={() => setOpenFaq(open ? -1 : i)}
                    className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
                    data-testid={`academy-faq-${i}`}>
                    <span className="text-cream">{f.q}</span>
                    <ChevronDown size={16} className={`text-gold transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && <div className="px-5 pb-5 text-cream-dim text-sm">{f.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Role Portals */}
      <section className="bg-navy-deep border-t border-gold-dim/20 py-14">
        <div className="container-x">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Role Portals</div>
          <h2 className="font-display text-3xl md:text-4xl mt-3">Sign in by role</h2>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {[
              { to: "/student-login",        Icon: GraduationCap, title: "Student Login",          sub: "Courses · assignments · grades · internship hours" },
              { to: "/teacher-aide-login",   Icon: Users2,        title: "Teacher's Aide Login",   sub: "Roster · grading · remediation · communication" },
              { to: "/parent-login",         Icon: HandHeart,     title: "Parent Login",           sub: "Progress · scores · alerts · attendance" },
            ].map(({ to, Icon, title, sub }) => (
              <Link key={to} to={to} className="glass p-6 hover:border-gold transition-colors block" data-testid={`portal-card-${to.replace(/\W/g, '-')}`}>
                <Icon size={24} className="text-gold mb-3" />
                <div className="font-display text-lg">{title}</div>
                <div className="text-cream-dim text-sm mt-1">{sub}</div>
                <div className="text-electric text-xs mt-3 flex items-center gap-1">Sign in <ArrowRight size={12} /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CrownInstituteAcademy;
